// React import removed - using new JSX transform
import { NavLink } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  UtensilsCrossed,
  Settings,
  ChevronLeft,
  ChevronRight,
  Map,
  Bell,
  GanttChart,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { APP_NAME } from '../../lib/config'
import { useNotifications } from '../../contexts/NotificationsContext'

const navigation = [
  { 
    name: 'Αρχική', 
    href: '/', 
    icon: LayoutDashboard,
    description: 'Επισκόπηση εστιατορίου'
  },
  { 
    name: 'Κρατήσεις', 
    href: '/reservations', 
    icon: Calendar,
    description: 'Διαχείριση κρατήσεων'
  },
  { 
    name: 'Τραπέζια', 
    href: '/tables', 
    icon: UtensilsCrossed,
    description: 'Κατάσταση τραπεζιών'
  },
  { 
    name: 'Πελάτες', 
    href: '/customers', 
    icon: Users,
    description: 'Διαχείριση πελατών'
  },
  { 
    name: 'Πλάνο Ορόφου', 
    href: '/floor-plan', 
    icon: Map,
    description: 'Ζωντανή προβολή πλάνου'
  },
  {
    name: 'Χρονοδιάγραμμα',
    href: '/timeline',
    icon: GanttChart,
    description: 'Timeline κρατήσεων'
  },
  {
    name: 'Ειδοποιήσεις',
    href: '/notifications',
    icon: Bell,
    description: 'Νέες κρατήσεις & αλλαγές'
  },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { unreadCount } = useNotifications()

  return (
    <div className={cn(
      "bg-background border-r border-border shadow-lg flex flex-col transition-all duration-300 items-center h-full overflow-y-auto overflow-x-hidden sidebar-scroll",
      collapsed ? "w-16" : "w-[13rem] lg:w-56"
    )} data-collapsed={collapsed ? 'true' : 'false'}>
      {/* Logo & Toggle */}
      <div className={cn("border-b border-border relative", collapsed ? "py-4" : "p-6")}>
        {/* Expanded State */}
        {!collapsed && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                <UtensilsCrossed className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">{APP_NAME}</h1>
                <p className="text-sm text-muted-foreground">Εστιατόριο</p>
              </div>
            </div>
            {/* Toggle Button - Expanded */}
            <button
              onClick={onToggle}
              className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white border border-gray-200 shadow-sm p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Collapse Sidebar"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}
        
        {/* Collapsed State - Vertical Stack */}
        {collapsed && (
          <div className="flex flex-col items-center space-y-3">
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <UtensilsCrossed className="w-5 h-5 text-white" />
            </div>
            {/* Toggle Button - Collapsed */}
            <button
              onClick={onToggle}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Expand Sidebar"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navigation.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center text-sm font-medium rounded-lg transition-colors touch-target',
                  collapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                )
              }
              title={collapsed ? item.name : undefined}
            >
              {({ isActive }) => (
                <>
                  <Icon 
                    className={cn(
                      'h-5 w-5 transition-colors',
                      collapsed ? 'mr-0' : 'mr-3',
                      isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-accent-foreground'
                    )} 
                  />
                  {!collapsed && (
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        <span>{item.name}</span>
                        {item.href === '/notifications' && unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] leading-none">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </div>
                      <div className={cn(
                        'text-xs',
                        isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      )}>
                        {item.description}
                      </div>
                    </div>
                  )}
                  {collapsed && item.href === '/notifications' && unreadCount > 0 && (
                    <span className="absolute -mt-4 ml-5 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Settings */}
      <div className="p-4 border-t border-border">
        <NavLink
          to="/settings"
          className={cn(
            "group flex items-center text-sm font-medium text-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors touch-target",
            collapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3'
          )}
          title={collapsed ? "Ρυθμίσεις" : undefined}
        >
          <Settings className={cn(
            "h-5 w-5 text-muted-foreground group-hover:text-accent-foreground",
            collapsed ? 'mr-0' : 'mr-3'
          )} />
          {!collapsed && <span>Ρυθμίσεις</span>}
        </NavLink>
      </div>
    </div>
  )
}

export default Sidebar 